import { useParams } from "react-router-dom";
import {
  Course,
  CourseStatus,
  useCompleteCourseFunctionRequest,
  useGetCourseFunctionRequest,
  useUnCompleteCourseFunctionRequest,
  useStartCourseCompletionStatusFunctionRequest,
} from "shared/request/myHealthyAdvantageApi";
import { Chip } from "shared/UI/Chip/Chip";
import { NarrowContainer } from "shared/UI/LayoutComponents/LayoutComponents";
import { PageHeader } from "UIPalette/PageHeader/PageHeader";
import { ReactComponent as InfoIcon } from "shared/assets/Icons/info-icon.svg";
import { DatoContent } from "UIPalette/Components/DatoContent/DatoContent";
import Button from "shared/UI/Buttons/Button";
import { useTranslation } from "react-i18next";
import PageTitle from "UIPalette/PageTitle/PageTitle";
import { ClickableRoundedCard } from "../../../Support/Components/ContactCard/ClickableRoundedCard";
import { ReactComponent as GreenTickIcon } from "shared/assets/Icons/ticks/green.svg";
import classNames from "classnames";
import { ClientInfoState } from "shared/core/state/clientInfoState";
import { useOptimistic } from "../../../../hooks/useOptimistic/useOptimistic";
import { ViewStateWrapper } from "UIPalette/ViewStateWrapper/ViewStateWrapper";
import { trackEvents } from "core/monitoring/Events";
import { EventNames } from "core/monitoring/types/enums";
import { externalLink } from "core/links";
import { useAppReviewContext } from "core/state/AppReviewContext/AppReviewContext";

export const WisdomAcademyCourseDetails = () => {
  const xClientCode = ClientInfoState.getCode();
  const { t } = useTranslation();
  const { courseId } = useParams();
  const {
    data: courseData,
    isLoading: isCourseLoading,
    error: courseError,
    mutate: fetchCourse,
  } = useGetCourseFunctionRequest(
    { id: courseId, xClientCode },
    { revalidateOnMountIfStale: false, showErrorIfStale: false, shouldFetch: courseId !== undefined }
  );
  const { trigger: completeCourse } = useCompleteCourseFunctionRequest({ xClientCode });
  const { trigger: unCompleteCourse } = useUnCompleteCourseFunctionRequest({ xClientCode });
  const { trigger: markCourseAsStarted } = useStartCourseCompletionStatusFunctionRequest();
  const { handlePositiveMoment } = useAppReviewContext();

  const [course, applyCourseDataOptimistic, resetCourseDataOptimistic, finishCourseDataOptimistic] = useOptimistic<
    Course | undefined,
    CourseStatus | undefined
  >(courseData, (prev, newStatus) => {
    if (prev && newStatus) {
      return { ...prev, status: newStatus };
    }
  });

  const eventProperties = { title: course?.title ?? "", id: course?.id ?? "" };

  const isComplete = () => course?.status === "Completed";
  const hasNeverStarted = () => course?.status === "NeverStarted";

  const handleCourseCompletion = async (complete: boolean) => {
    applyCourseDataOptimistic(complete ? "Completed" : "NotStarted");

    try {
      const query = {
        query: {
          courseId: course?.id ?? "",
        },
      };
      if (complete) {
        trackEvents(EventNames.WISDOM_ACADEMY_COURSE_COMPLETE, eventProperties);
        await completeCourse(query);
        handlePositiveMoment();
      } else {
        trackEvents(EventNames.WISDOM_ACADEMY_COURSE_UNCOMPLETE, eventProperties);
        await unCompleteCourse(query);
      }
      await fetchCourse();
      finishCourseDataOptimistic();
    } catch (error) {
      resetCourseDataOptimistic();
    }
  };

  const handleCourseStart = async (courseId: string) => {
    applyCourseDataOptimistic("Started");
    try {
      await markCourseAsStarted({ query: { CourseId: courseId } });
      await fetchCourse();
      finishCourseDataOptimistic();
    } catch {
      resetCourseDataOptimistic();
    }
  };

  const renderCompletionOverlay = () => (
    <div
      data-testid="image-completion-overlay"
      className={classNames("transition-all duration-500 ease-in-out ", isComplete() ? "opacity-100" : "opacity-0")}
    >
      <div className="absolute right-0 bottom-0 w-full h-full bg-[linear-gradient(to_top_left,rgba(0,0,0,1)_0%,transparent_60%)]" />
      <div className="absolute right-0 bottom-0 p-4 pointer-events-none">
        <Chip theme="tertiary" boldText text={t("wisdomAcademy.course.courseComplete")} />
      </div>
    </div>
  );

  const renderImageSection = () => (
    <div className="relative rounded-md overflow-hidden">
      <img
        className="h-[200px] lg:h-[300px] w-full object-cover"
        srcSet={course?.image?.responsiveImage?.webpSrcSet}
        alt={course?.image?.responsiveImage?.alt || course?.title}
      />
      {renderCompletionOverlay()}
    </div>
  );

  const renderInfoNote = () => (
    <div className="flex gap-6 items-center">
      <InfoIcon className="shrink-0 text-navy-blue h-7 w-7" />
      <p>{t("wisdomAcademy.course.pleaseNote")}</p>
    </div>
  );

  const renderCompletionCard = () => (
    <ClickableRoundedCard
      className={classNames({ "opacity-50": hasNeverStarted() })}
      disabled={hasNeverStarted()}
      handleClick={() => handleCourseCompletion(!isComplete())}
      renderChildrenOnly
    >
      <div className="flex justify-between items-center">
        <p className="font-bold">{t("wisdomAcademy.course.markComplete")}</p>
        {isComplete() ? <GreenTickIcon className="w-7 h-7" /> : <div className="w-7 h-7 rounded-full inset-shadow-md transition-all" />}
      </div>
    </ClickableRoundedCard>
  );
  const handleOpenCourse = () => {
    if (course?.id) {
      externalLink(course.url);
      handleCourseStart(course.id);
      trackEvents(EventNames.WISDOM_ACADEMY_COURSE_STARTED, eventProperties);
    }
  };

  return (
    <>
      <PageTitle title={t("pageTitle.wisdomAcademy.course", { title: course?.title })} />

      <ViewStateWrapper
        loading={isCourseLoading}
        error={!!courseError}
        errorMessage={t("wisdomAcademy.course.courseNotFoundErrorMessage")}
        onRetry={fetchCourse}
        isFullScreenView
      >
        <PageHeader title={course?.title} narrowView />
        <NarrowContainer className="space-y-6">
          <p dangerouslySetInnerHTML={{ __html: t("wisdomAcademy.course.courseDuration", { duration: String(course?.timeEstimate || 0) }) }} />

          {renderImageSection()}

          <Button text={t(isComplete() ? "wisdomAcademy.course.restartCourse" : "wisdomAcademy.course.openCourse")} onClick={handleOpenCourse} />

          {renderInfoNote()}

          <hr className="text-brand-grey-light" />

          {renderCompletionCard()}
          <DatoContent content={course?.description} />
        </NarrowContainer>
      </ViewStateWrapper>
    </>
  );
};
